import classNames from 'classnames';
import React, { useContext, useRef, useState } from 'react';
import formatDuration from '../../../utilities/formatDuration';
import { DragType } from '../../../models/dragTypes';
import PlaylistItem from '../../../models/playlistItem/playlistItem';
import SoundCloudApiTrack from '../../../models/soundcloud/soundCloudApiTrack';
import Source, { Sources } from '../../../models/source';
import Track from '../../../models/track';
import SoundCloudTrack from '../../../models/track/soundCloudTrack';
import { ActivePlaylistContext } from '../../../services/activePlaylist/activePlaylist';
import PlaylistColumns, {
  ColumnKey,
  ColumnScope,
  TrackProperty,
} from '../../../services/columnSettings/playlistColumns';
import { Context } from '../../../services/_globalContext/context';
import { preventDefaultStopPropogation } from '../../../utilities/preventDefault';
import AddToPlaylistSubMenu from '../../common/addToPlaylist/addToPlaylistSubMenu';
import FadilaMenuItem from '../../common/contextMenu/fadilaMenuItem';
import useColumnSettings from '../../common/hooks/useColumnSettings';
import useSelectedItemsRef from '../../common/hooks/useSelectedItemsRef';
import s from '../../common/itemRow/itemRow.module.scss';
import ItemRowWrapper from '../../common/itemRow/ItemRowWrapper';
import { openOnSoundCloud } from '../openOnSoundCloud';
import sc from '../soundCloud.module.scss';

export interface SoundCloudTrackRowProps {
  /** Item array index */
  index: number;
  item: SoundCloudApiTrack;
  handlePlayTrack(item: SoundCloudApiTrack): void;
  addItemsThenToLibrary(
    addItems: (i: PlaylistItem[]) => void,
  ): (...items: SoundCloudApiTrack[]) => void;

  isSelected: boolean;
  selectedItemsRef: React.MutableRefObject<Iterable<SoundCloudApiTrack>>;

  handleToggleItem(plItm: SoundCloudApiTrack): void;
  changeItemSelectionState(
    plItm: SoundCloudApiTrack,
    selectState: boolean,
    removePrevious?: boolean,
  ): void;
  handleShiftSelect(plItm: SoundCloudApiTrack): void;
  clearSelected(): void;

  portalTarget?: HTMLElement;
}

/**
 * One track in the SoundCloud search results. The Spotify row with the
 * SoundCloud shapes, plus the two things the API terms ask for on every
 * track shown: the uploader as the credit (the artist column) and a link
 * to the track's page on soundcloud.com (in the menu).
 */
export default function TrackSearchResult({
  index,
  item,
  handlePlayTrack,
  addItemsThenToLibrary,

  portalTarget,
  isSelected,
  selectedItemsRef,
  changeItemSelectionState,
  handleShiftSelect,
  handleToggleItem,
  clearSelected,
}: SoundCloudTrackRowProps) {
  const titleElRef = useRef<HTMLTableDataCellElement>(null);
  const selectedItemsRefWithMapping = useSelectedItemsRef<SoundCloudApiTrack, SoundCloudTrack>(
    SoundCloudTrack.from,
    selectedItemsRef,
  );
  const ctx = useContext(Context);
  const apCtx = useContext(ActivePlaylistContext);

  const [isDragging, setIsDragging] = useState(false);

  const { columns, columnSettings } = useColumnSettings(ColumnScope.SoundCloudTrackSearch);

  const onDragStart = (ev: React.DragEvent) => {
    console.debug('dragstart on track item');

    setIsDragging(true);

    // We set loads of data here since you can't call getData in onDragOver
    ev.dataTransfer.setData('dragType', DragType.TrackItems);
    // types.includes checks for matching drag type in onDragOver
    ev.dataTransfer.setData(DragType.TrackItems, DragType.TrackItems);
    ev.dataTransfer.setData('text/plain', item.urn);
    ev.dataTransfer.setData('name', item.title);

    ev.dataTransfer.effectAllowed = 'copy';

    if (titleElRef.current) {
      ev.dataTransfer.setDragImage(titleElRef.current, 0, 0);
    }
    if (!isSelected) {
      clearSelected();
      changeItemSelectionState(item, true, true);
    }

    ctx.dndData[item.urn] = selectedItemsRefWithMapping;
  };
  function onDragEnd(_ev: React.DragEvent) {
    setIsDragging(false);

    delete ctx.dndData[item.urn];
  }

  return (
    <ItemRowWrapper<SoundCloudApiTrack>
      index={index}
      item={item}
      itemId={item.urn}

      onDragStart={onDragStart}
      onDragEnd={onDragEnd}

      isSelected={isSelected}
      isDragging={isDragging}

      handleShiftSelect={() => handleShiftSelect(item)}
      handleToggleItem={() => handleToggleItem(item)}
      selectOnlyItem={() => changeItemSelectionState(item, true, true)}

      handlePlay={() => handlePlayTrack(item)}

      menuItems={
        <>
          <FadilaMenuItem
            onClick={preventDefaultStopPropogation(() => handlePlayTrack(item))}
            attributes={{
              className: 'secondary',
            }}
          >
            Play
          </FadilaMenuItem>
          <AddToPlaylistSubMenu
            newPlaylistName={item.title}
            playlistItemOrItems={(addItems) =>
              addItemsThenToLibrary(addItems)(...selectedItemsRef.current)
            }
          />
          <FadilaMenuItem
            onClick={preventDefaultStopPropogation(() => openOnSoundCloud(item.permalink_url))}
          >
            Open on SoundCloud
          </FadilaMenuItem>
        </>
      }
      portalTarget={portalTarget}
    >
      {columnSettings && (
        <>
          <div className={classNames(s.rowCell, s.playBtn)}>
            {apCtx.currentTrack.id === item.urn ? (
              '♫'
            ) : (
              <button
                className="play-button invisible-control"
                onClick={preventDefaultStopPropogation(() => handlePlayTrack(item))}
              >
                ►
              </button>
            )}
          </div>
          {columns.map((col) => {
            const colKey = col as ColumnKey;

            let value: string | undefined;
            let content: React.ReactNode;

            switch (colKey) {
              case TrackProperty.length:
                value = formatDuration(item.duration);
                break;

              case TrackProperty.title:
                value = item.title;
                content = (
                  <>
                    {item.title}
                    {item.access === 'preview' && (
                      <span
                        className={sc.badge}
                        title="SoundCloud only offers a 30-second preview of this track"
                      >
                        preview
                      </span>
                    )}
                  </>
                );
                break;

              case TrackProperty.artist:
                value = item.user ? item.user.username : '';
                break;

              case TrackProperty.album:
                value = '';
                break;

              // The same name as the artist column: SoundCloud has no artist
              // field, so both are the uploader. Hidden by default, and here
              // because the user can unhide it.
              case TrackProperty.uploader:
                value = item.user ? item.user.username : '';
                break;

              case TrackProperty.genre:
                value = item.genre || '';
                break;

              case TrackProperty.releaseDate:
                value = Track.format(
                  SoundCloudTrack.from(item).releaseDate,
                  TrackProperty.releaseDate,
                );
                break;

              case TrackProperty.type:
                value = Sources[Source.SoundCloud].name;
                break;

              case TrackProperty.dateAdded:
                value = '';
                break;

              default:
                throw new Error('Unexpected column mismatch!');
            }

            return (
              <div
                key={colKey}
                className={classNames(s.rowCell, {
                  [s.rightAlignCell]: PlaylistColumns[colKey].alignRight,
                })}
                ref={colKey === TrackProperty.title ? titleElRef : undefined}
                style={{
                  width: columnSettings[colKey]?.width,
                }}
                title={value}
              >
                {content || value}
              </div>
            );
          })}
        </>
      )}
    </ItemRowWrapper>
  );
}
